import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import RefreshIcon from '@mui/icons-material/Refresh';

type Props = {
  error: Error;
  onRetry: () => void;
};

/**
 * Error fallback component displayed when configuration fails to load
 */
function ConfigErrorFallback({ error, onRetry }: Props) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: 3,
        backgroundColor: '#f5f5f5',
      }}
    >
      <Box sx={{ maxWidth: 600, width: '100%' }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle sx={{ fontWeight: 'bold' }}>
            Configuration Error
          </AlertTitle>
          Unable to load application configuration. Please check your network
          connection and try again.
        </Alert>

        <Box
          sx={{
            backgroundColor: 'white',
            padding: 3,
            borderRadius: 1,
            boxShadow: 1,
          }}
        >
          <Typography variant="h5" gutterBottom>
            Application Configuration Failed
          </Typography>

          <Typography variant="body1" color="text.secondary" paragraph>
            The application could not retrieve the required configuration from
            the server. This may be due to network issues or server
            unavailability.
          </Typography>

          <Box
            sx={{
              backgroundColor: '#f5f5f5',
              padding: 2,
              borderRadius: 1,
              mb: 3,
            }}
          >
            <Typography
              variant="body2"
              component="pre"
              sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'monospace',
                margin: 0,
                color: 'error.main',
              }}
            >
              {error.message}
            </Typography>
          </Box>

          <Button
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={onRetry}
            fullWidth
            size="large"
          >
            Retry Configuration Load
          </Button>

          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ mt: 2 }}
          >
            If the problem persists, please contact your system administrator.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default ConfigErrorFallback;
